import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import { useApp } from "../context/AppContext";
import "./Biblioteca.css";

function timeAgo(isoDate) {
  const diff = Math.floor((Date.now() - new Date(isoDate + "Z")) / 1000);
  if (diff < 60) return "ahora mismo";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} días`;
}

function ImageCard({ item, onEdit, onDownload, onDelete }) {
  const label = item.id;
  const author = item.author;
  const ago = timeAgo(item.created_at);

  return (
    <div className="bib-card">
      <div className="bib-thumb">
        <img
          src={`${API_URL}/images/${encodeURIComponent(item.id)}/file`}
          alt={label}
          loading="lazy"
        />
      </div>
      <div className="bib-meta">
        <div className="bib-meta-left">
          <span className="bib-id">{label}</span>
          <span className="bib-detail">{author} · {ago}</span>
        </div>
        <div className="bib-meta-actions">
          <button className="bib-icon-btn bib-icon-edit" onClick={(e) => { e.stopPropagation(); onEdit(item); }} title="Editar">✎</button>
          <button className="bib-icon-btn bib-icon-download" onClick={(e) => { e.stopPropagation(); onDownload(item); }} title="Descargar">↓</button>
          <button className="bib-icon-btn bib-icon-delete" onClick={(e) => { e.stopPropagation(); onDelete(item); }} title="Eliminar">✕</button>
        </div>
      </div>
    </div>
  );
}

function DeleteModal({ item, onCancel, onConfirm }) {
  if (!item) return null;
  return (
    <div className="bib-modal-overlay" onClick={onCancel}>
      <div className="bib-modal-box" onClick={e => e.stopPropagation()}>
        <h3>¿Eliminar esta imagen?</h3>
        <p>Esta acción no se puede deshacer. Se eliminará la imagen y sus metadatos de la biblioteca.</p>
        <div className="bib-modal-id">{item.id}</div>
        <div className="bib-modal-actions">
          <button className="bib-modal-cancel" onClick={onCancel}>Cancelar</button>
          <button className="bib-modal-delete" onClick={onConfirm}>Eliminar</button>
        </div>
      </div>
    </div>
  );
}

export default function Biblioteca({ token }) {
  const navigate = useNavigate();
  const { setIdea, setChatHistory, setPromptEng, setPromptEsp, setMdvObject, setMotor, setResumen, setEditImageId } = useApp();
  const [items, setItems]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [pages, setPages]           = useState(1);
  const [authorFilter, setAuthorFilter] = useState("");
  const [search, setSearch]         = useState("");
  const [loading, setLoading]       = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, per_page: 24 });
      if (authorFilter) params.set("author", authorFilter);
      const res = await fetch(`${API_URL}/images?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items);
        setTotal(data.total);
        setPage(data.page);
        setPages(data.pages);
      }
    } finally {
      setLoading(false);
    }
  }, [authorFilter, token]);

  useEffect(() => { load(1); }, [load]);

  const filtered = search.trim()
    ? items.filter(i => i.id.toLowerCase().includes(search.toLowerCase()))
    : items;

  const handleDownload = (item) => {
    const a = document.createElement("a");
    a.href = `${API_URL}/images/${encodeURIComponent(item.id)}/file`;
    a.download = `${item.id.replace("#", "")}.png`;
    a.click();
  };

  const handleEdit = (item) => {
    // Limpiar chat anterior para que Prompt IN sea editable
    setChatHistory([]);
    // Cargar resumen como idea inicial
    setIdea(item.resumen || item.prompt || "");
    // Cargar prompt en Prompter
    setPromptEng(item.prompt || "");
    setPromptEsp("");
    // Cargar MDV (siempre limpiar primero, luego cargar si existe)
    setMdvObject(null);
    if (item.mdv_json) {
      try { setMdvObject(JSON.parse(item.mdv_json)); } catch {}
    }
    // Seleccionar motor original
    const motorMap = { "flux2": "flux2" };
    setMotor(motorMap[item.motor] || "sdxl");
    setResumen(item.resumen || "");
    // Cargar imagen en Prompter
    setEditImageId(item.id);
    // Navegar a Prompter
    navigate("/keyframes/prompter");
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await fetch(`${API_URL}/images/${encodeURIComponent(deleteTarget.id)}`, {
        method: "DELETE",
        headers,
      });
      setDeleteTarget(null);
      load(page);
    } catch (e) {
      console.error("Error al eliminar:", e);
    }
  };

  return (
    <div className="bib-root">
      <div className="bib-main">
        {/* Toolbar */}
        <div className="bib-toolbar">
          <h2 className="bib-title">Biblioteca <span className="bib-count">{total} imágenes</span></h2>
          <div className="bib-filters">
            <input
              className="bib-search"
              placeholder="Buscar por ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <input
              className="bib-search"
              placeholder="Filtrar por autor..."
              value={authorFilter}
              onChange={(e) => { setAuthorFilter(e.target.value); load(1); }}
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="bib-empty">Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="bib-empty">No hay imágenes generadas todavía.</div>
        ) : (
          <div className="bib-grid">
            {filtered.map((item) => (
              <ImageCard
                key={item.id}
                item={item}
                onEdit={handleEdit}
                onDownload={handleDownload}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
        )}

        {/* Paginación */}
        {pages > 1 && (
          <div className="bib-pagination">
            <button disabled={page <= 1} onClick={() => load(page - 1)}>‹</button>
            <span>{page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => load(page + 1)}>›</button>
          </div>
        )}
      </div>

      {/* Modal de confirmación de borrado */}
      <DeleteModal
        item={deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
